import { useProductPageStore } from '@hooks/states/useProductPageStore';
import { AssociationType } from '@libs/associations';
import { CategoryHelper } from '@libs/category.helper';
import { getLocale, QUERY_KEYS } from '@libs/constants';
import { getPriceCountry } from '@libs/get-price-country.helper';
import { nextApiCall } from '@libs/nextApiCall';
import { nextApiCallErrorFn } from '@libs/nextApiCallErrorFn';
import type { ModelAssociationFragmentEnhanced } from '@models/enhanced-fragments';
import { getAssociatedModelIdsFromProducts } from '@product/ProductPage/PageLayouts/getAssociatedModelIdsFromProducts';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';
import { useTranslation } from 'next-i18next';
import { ToastType, useToasts } from '@hooks/useToasts';

export const useShelvesProductAssociationModelsQuery = () => {
  const router = useRouter();
  const locale = getLocale(router);
  const currentProduct = useProductPageStore(state => state.currentProduct);
  const primaryCategoryCode = useProductPageStore(state => state.primaryCategoryCode);
  const priceCountry = getPriceCountry(locale);
  const { t } = useTranslation(['layout/header', 'toast/toast']);
  const { addToast } = useToasts();

  return useQuery<ModelAssociationFragmentEnhanced[]>(
    [QUERY_KEYS.SHELVES_MODELS_ASSOCIATIONS, currentProduct.id],
    async () => {
      if (!CategoryHelper.isEchain(primaryCategoryCode)) {
        return [];
      }

      const shelvesProductAssociationModelIds = getAssociatedModelIdsFromProducts(
        [currentProduct],
        AssociationType.SHELVES
      );

      if (!shelvesProductAssociationModelIds || shelvesProductAssociationModelIds.length === 0) {
        return [];
      }

      const response = await nextApiCall('shelves-association-models', {
        shelvesProductAssociationModelIds,
        locale,
        priceCountry,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product shelves association models for ${currentProduct.id}`);
      }

      return (await response.json()) as ModelAssociationFragmentEnhanced[];
    },
    {
      onError: err => {
        addToast({ content: t('TOAST.ERROR_OCCURRED', { ns: 'toast/toast' }), type: ToastType.ERROR });
        nextApiCallErrorFn(err, `Fetch product shelves association models for ${currentProduct.id} failed`);
      },
    }
  );
};
